/**
 * Formatação de dinheiro para a tela. O valor chega como string decimal do backend e só é
 * convertido aqui, na exibição — nenhuma conta financeira acontece no front.
 */
const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export function formatMoney(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return BRL.format(Number(value));
}

/**
 * Valor decimal negativo (prejuízo), lido na string que a API manda — sem virar `number`, que é a
 * regra do domínio para dinheiro. "-0.00" não é prejuízo.
 */
export function isNegative(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith('-') && /[1-9]/.test(trimmed);
}

/**
 * Dólar, só para o custo de IA — a moeda em que o provedor cobra (ADR-029). Casas variáveis:
 * uma chamada custa frações de centavo, e arredondar para 2 casas mostraria US$ 0,00.
 */
export function formatUsd(value: string | null | undefined, digits = 2): string {
  if (value === null || value === undefined) return '—';
  return `US$ ${Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

/**
 * Converte o que a pessoa digitou ("12,50", "1.234,56", "12.5") para o formato da API
 * ("12.50"). `null` quando não é um valor válido.
 */
export function parseMoneyInput(input: string): string | null {
  const cleaned = input.trim().replace(/\s|R\$/g, '');
  if (cleaned === '') return null;
  const normalized = cleaned.includes(',') ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned;
  return /^\d{1,10}(\.\d{1,4})?$/.test(normalized) ? normalized : null;
}

/** Número decimal vindo da API (string com ponto) exibido em pt-BR, com casas fixas. */
export function formatDecimal(value: string | null | undefined, digits: number): string {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Fração da API ("0.067280") exibida como percentual pt-BR ("6,73%"). */
export function formatPercent(fraction: string | null | undefined, digits = 2): string {
  if (fraction === null || fraction === undefined) return '—';
  return `${(Number(fraction) * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;
}

/**
 * Percentual digitado ("6", "6,5", "11,33%") para fração da API ("0.06", "0.065"). A tela
 * fala em %, a API em fração — a conversão mora aqui, num lugar só, porque é exatamente onde
 * o 16 vira 1.600%.
 */
export function parsePercentInput(input: string): string | null {
  const cleaned = input.trim().replace('%', '').replace(',', '.');
  if (!/^\d{1,2}(\.\d{1,4})?$/.test(cleaned)) return null;
  const fraction = Number(cleaned) / 100;
  return fraction < 1 ? fraction.toFixed(6).replace(/0+$/, '').replace(/\.$/, '') : null;
}

/**
 * Fração da API ("0.145") para o campo de percentual ("14,5"), deslocando a vírgula no texto —
 * `Number("0.145") * 100` dá 14.499999999999998, e o campo cortava para 14,49 ao salvar.
 */
export function fractionToPercentInput(fraction: string | null): string {
  if (fraction === null) return '';
  const [integer = '0', decimals = ''] = fraction.trim().split('.');
  const padded = `${decimals}00`;
  const whole = `${integer}${padded.slice(0, 2)}`.replace(/^0+(?=\d)/, '');
  const rest = padded.slice(2).replace(/0+$/, '');
  return rest ? `${whole},${rest}` : whole;
}

/** "-12.50" → partes para comparar sem virar `number`; zero negativo conta como zero. */
function decimalParts(value: string) {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const [integer = '', fraction = ''] = trimmed.replace(/^[-+]/, '').split('.');
  const int = integer.replace(/^0+/, '');
  const frac = fraction.replace(/0+$/, '');
  return { negative: negative && (int !== '' || frac !== ''), int, frac };
}

/**
 * Compara dois valores decimais em texto ("89.90", "-3.10") para ordenar tabela — sem converter
 * para `number` (regra do domínio) e sem a ordem de texto, em que "100.00" vem antes de "9.00".
 */
export function compareDecimal(a: string, b: string): number {
  const x = decimalParts(a);
  const y = decimalParts(b);
  if (x.negative !== y.negative) return x.negative ? -1 : 1;
  const sign = x.negative ? -1 : 1;
  if (x.int.length !== y.int.length) return sign * (x.int.length < y.int.length ? -1 : 1);
  if (x.int !== y.int) return sign * (x.int < y.int ? -1 : 1);
  const size = Math.max(x.frac.length, y.frac.length);
  const fx = x.frac.padEnd(size, '0');
  const fy = y.frac.padEnd(size, '0');
  if (fx !== fy) return sign * (fx < fy ? -1 : 1);
  return 0;
}
